import React, { useState, useMemo } from 'react';
import { Save, Sun, Database, MessageCircle, Copy, X } from 'lucide-react';
import { pumpConfigurations, mockMorningReadings, tanks } from '../mockData';

const MorningReadings = () => {
  const [date, setDate] = useState(mockMorningReadings.date);
  const [readings, setReadings] = useState(mockMorningReadings.readings);
  const [testing, setTesting] = useState({});
  const [tankReadings, setTankReadings] = useState({});
  const [showWhatsappModal, setShowWhatsappModal] = useState(false);
  const [whatsappText, setWhatsappText] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  // Flatten the pump config into a list of nozzles for the table
  const allNozzles = useMemo(() => {
    return pumpConfigurations.flatMap(pump => 
      pump.nozzles.map(nozzle => {
        const currentMock = mockMorningReadings.readings[pump.pumpId]?.[nozzle.id] || 100000;
        const yesterdayReading = currentMock - (Math.floor(Math.random() * 2000) + 1000); // Mock previous reading
        
        return {
          pumpId: pump.pumpId,
          nozzleId: nozzle.id,
          type: nozzle.type,
          yesterdayReading,
        };
      })
    );
  }, []);

  const handleReadingChange = (pumpId, nozzleId, value) => {
    setIsSaved(false);
    setReadings({
      ...readings,
      [pumpId]: {
        ...readings[pumpId],
        [nozzleId]: value
      }
    });
  };

  const handleTestingChange = (pumpId, nozzleId, value) => {
    setIsSaved(false);
    setTesting({
      ...testing,
      [pumpId]: {
        ...testing[pumpId],
        [nozzleId]: value
      }
    });
  };

  const handleTankReadingChange = (tankId, value) => {
    setIsSaved(false);
    setTankReadings({
      ...tankReadings,
      [tankId]: value
    });
  };

  const handleSave = () => {
    // In a real app, this would make an API call to save the data
    setIsSaved(true);
  };

  const generateWhatsappReport = () => {
    let msSale = 0;
    let hsdSale = 0;
    let xgSale = 0;

    allNozzles.forEach(item => {
      const todayReading = parseFloat(readings[item.pumpId]?.[item.nozzleId]);
      const testingLiters = parseFloat(testing[item.pumpId]?.[item.nozzleId]) || 0;
      if (!isNaN(todayReading) && todayReading >= item.yesterdayReading) {
        const sale = todayReading - item.yesterdayReading - testingLiters;
        if (item.type === 'Petrol') msSale += sale;
        else if (item.type === 'Diesel') hsdSale += sale;
        else if (item.type === 'Xtragreen') xgSale += sale;
      }
    });

    const totalSale = msSale + hsdSale + xgSale;

    const selectedDate = new Date(date);
    const yesterdayDate = new Date(selectedDate);
    yesterdayDate.setDate(selectedDate.getDate() - 1);
    
    const formatDate = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getFullYear()).slice(2)}`;

    const text = `${formatDate(yesterdayDate)}
SALE
MS        :- ${msSale.toFixed(0)}
HSD     :- ${hsdSale.toFixed(0)}
XG.       :- ${xgSale.toFixed(0)}
TOTAL: ${totalSale.toFixed(0)}

${formatDate(selectedDate)}
OPENING STOCK
MS         :- ${tankReadings[1] || '0'}
HSD  1    :- ${tankReadings[2] || '0'}
HSD.  2.  :- ${tankReadings[3] || '0'}
XG.          :- ${tankReadings[4] || '0'}`;

    setWhatsappText(text);
    setShowWhatsappModal(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(whatsappText);
    alert('Copied to clipboard!');
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Start of Day Readings</h1>
          <p className="text-slate-500 text-sm mt-1">Record the 6:00 AM baseline meter readings for all nozzles and tank stocks</p>
        </div>
        <div className="flex gap-3 items-center">
          <button 
            onClick={generateWhatsappReport}
            disabled={!isSaved}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm ${
              isSaved 
                ? 'bg-[#25D366] hover:bg-[#1ebd5b] text-white' 
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            <MessageCircle className="w-4 h-4" /> WhatsApp Report
          </button>
          <button 
            onClick={handleSave}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors shadow-sm ${
              isSaved ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'btn-primary'
            }`}
          >
            {isSaved ? <Save className="w-4 h-4" /> : <Save className="w-4 h-4" />} 
            {isSaved ? 'Saved' : 'Save Baseline'}
          </button>
        </div>
      </div>

      <div className="glass-panel p-6 mb-6 flex items-center gap-4">
        <Sun className="w-8 h-8 text-amber-500 shrink-0" />
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Business Date</label>
          <input 
            type="date" 
            className="input-field max-w-[200px]"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
        <div className="hidden sm:block ml-auto bg-amber-50 text-amber-700 px-4 py-2 rounded-lg text-sm font-medium border border-amber-200">
          These readings set the Opening Balance for {date}
        </div>
      </div>

      {/* Underground Tanks Section */}
      <div className="glass-panel overflow-hidden mb-6">
        <div className="bg-slate-50 border-b border-slate-100 p-4">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-500" /> Underground Tank Stock (Morning Dip Reading)
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tanks.map(tank => {
            const currentReading = tankReadings[tank.id] || '';
            const isValid = currentReading === '' || parseFloat(currentReading) <= tank.capacity;
            
            return (
              <div key={tank.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-1 h-full ${
                  tank.type === 'Petrol' ? 'bg-orange-500' :
                  tank.type === 'Diesel' ? 'bg-blue-500' : 'bg-emerald-500'
                }`}></div>
                
                <div className="flex justify-between items-start mb-4 pl-3">
                  <div>
                    <h3 className="font-bold text-slate-800">{tank.name}</h3>
                    <p className="text-xs font-semibold text-slate-500">{tank.type}</p>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded font-medium">
                    {tank.capacity.toLocaleString()} L
                  </span>
                </div>
                
                <div className="pl-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Dip Reading (Liters)</label>
                  <input 
                    type="number" 
                    className={`input-field font-mono font-bold text-lg ${!isValid ? 'border-red-300 bg-red-50 focus:border-red-500 text-red-700' : ''}`}
                    placeholder="e.g. 15000"
                    value={currentReading}
                    onChange={(e) => handleTankReadingChange(tank.id, e.target.value)}
                  />
                  {!isValid && <p className="text-xs text-red-500 mt-1 font-medium">Cannot exceed capacity!</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Nozzles Section */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 text-slate-600 text-sm border-b border-slate-200">
                <th className="p-4 font-semibold">Pump</th>
                <th className="p-4 font-semibold">Nozzle</th>
                <th className="p-4 font-semibold">Fuel Type</th>
                <th className="p-4 font-semibold">Yesterday's Reading</th>
                <th className="p-4 font-semibold">Today's 6:00 AM Reading</th>
                <th className="p-4 font-semibold">Testing (Liters)</th>
                <th className="p-4 font-semibold text-right">Net 24h Sales</th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {allNozzles.map((item) => {
                const todayReadingStr = readings[item.pumpId]?.[item.nozzleId];
                const todayReading = parseFloat(todayReadingStr);
                const testingLitersStr = testing[item.pumpId]?.[item.nozzleId];
                const testingLiters = parseFloat(testingLitersStr) || 0;
                
                const isReadingValid = !isNaN(todayReading) && todayReading >= item.yesterdayReading;
                const sales24h = isReadingValid ? (todayReading - item.yesterdayReading - testingLiters).toFixed(2) : '-';

                return (
                  <tr key={`${item.pumpId}-${item.nozzleId}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Pump {item.pumpId}</td>
                    <td className="p-4 font-medium text-slate-700">Nozzle {item.nozzleId}</td>
                    <td className="p-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-md ${
                        item.type === 'Petrol' ? 'bg-orange-100 text-orange-700' :
                        item.type === 'Diesel' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="p-4 text-slate-500 font-mono">
                      {item.yesterdayReading.toFixed(2)}
                    </td>
                    <td className="p-4">
                      <input 
                        type="number" 
                        className={`input-field max-w-[200px] font-mono font-bold ${todayReadingStr && !isReadingValid ? 'border-red-300 focus:border-red-500 bg-red-50' : 'border-primary-200 focus:border-primary-500'}`}
                        placeholder="Enter Reading"
                        value={todayReadingStr || ''}
                        onChange={(e) => handleReadingChange(item.pumpId, item.nozzleId, e.target.value)}
                      />
                    </td>
                    <td className="p-4">
                      <input 
                        type="number" 
                        className="input-field max-w-[120px] font-mono"
                        placeholder="0.00"
                        value={testingLitersStr || ''}
                        onChange={(e) => handleTestingChange(item.pumpId, item.nozzleId, e.target.value)}
                      />
                    </td>
                    <td className="p-4 text-right">
                      {sales24h !== '-' ? (
                        <span className="font-bold text-slate-900 bg-slate-100 px-3 py-1.5 rounded-lg">
                          {sales24h} L
                        </span>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* WhatsApp Modal */}
      {showWhatsappModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#25D366]" /> WhatsApp Summary
              </h3>
              <button 
                onClick={() => setShowWhatsappModal(false)}
                className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea 
                readOnly
                value={whatsappText}
                className="w-full h-64 p-4 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm resize-none focus:outline-none"
              ></textarea>
              <button 
                onClick={copyToClipboard}
                className="w-full mt-4 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white px-4 py-3 rounded-xl font-bold transition-colors"
              >
                <Copy className="w-5 h-5" /> Copy Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MorningReadings;
